import PropTypes from "prop-types";
import { Input } from "@vds/inputs";
import { ModalBody, ModalFooter, ModalTitle } from "@vds/modals";
import { Body, Title } from "@vds/typography";
import { useDispatch } from "react-redux";
import {
  fetchDeviceProfilesRequest,
  updateUI,
} from "../../../../store/actions/asset";
import { Icon } from "@vds/icons";
import { Select } from "antd";
import { ErrorMessage } from "../../../../global/components/ValidationIcon";
import { getLocationZoneList } from "../../common/utils/utils";

function EditAssetStep1(props) {
  const { opType, opHeaderText, formData, onExit } = props;

  const dispatch = useDispatch();
  const handleNext = () => {
    let errors = {
      step1: { ...formData?.step1?.errors },
      step2: { ...formData?.step2?.errors },
    };

    if (!formData?.step1?.assetName) {
      errors.step1.assetName = true;
    }

    if (!formData?.step2?.deviceType) {
      errors.step2.deviceType = true;
    }

    if (!formData?.step2?.deviceId?.id) {
      errors.step2.deviceId = true;
    }

    if (!formData?.step2?.deviceProfile?.id) {
      errors.step2.deviceProfile = true;
    }

    if (
      Object.values(errors?.step1).filter((error) => error === true).length >
        0 ||
      Object.values(errors?.step2).filter((error) => error === true).length > 0
    ) {
      dispatch(
        updateUI({
          formData: {
            ...formData,
            step1: { ...formData.step1, errors: errors.step1 },
            step2: { ...formData.step2, errors: errors.step2 },
          },
        })
      );
      return;
    }

    dispatch(updateUI({ formData: { ...formData, selectedStep: 2 } }));
  };

  const handleSelectChange = (name, value, bleserial = null) => {
    let error = {};
    if (name in formData?.step2?.errors) {
      error[name] = false;
    }

    let bleSerialObj = {};
    if (bleserial) {
      bleSerialObj = { bleserial };
    }

    dispatch(
      updateUI({
        formData: {
          ...formData,
          step2: {
            ...formData.step2,
            deviceId: { id: "", name: "Select device name" },
            deviceProfile: { id: "", name: "Select device profile" },
            [name]: value,
            ...bleSerialObj,
            errors: { ...formData.step2.errors, ...error },
          },
        },
      })
    );
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trimStart() : value; 
    let error = {};

    if (name in formData?.step1?.errors) {
      error[name] = false;
    }

    dispatch(
      updateUI({
        formData: {
          ...formData,
          step1: {
            ...formData.step1,
            errors: { ...formData.step1.errors, ...error },
            [name]: trimmedValue,
           // [name]: value,
          },
        },
      })
    );
  };

  const handleFAdditionalFieldChange = (e) => {
    const { name, value } = e.target;
    dispatch(
      updateUI({
        formData: { ...formData, step3: { ...formData.step3, [name]: value } },
      })
    );
  };

  const handleLocationZoneFieldChange = (key, name, value, locationObj) => {
    if (!locationObj) {
      dispatch(
        updateUI({
          formData: {
            ...formData,
            step3: {
              ...formData.step3,
              [name]: "",
              location: null,
            },
          },
        })
      );
      return;
    }
    const [_, floorId, zoneId] = key.split("=");
    const floor = locationObj?.floors?.find((f) => f.id === floorId);
    if (!floor) {
      console.error("Floor not found for ID:", floorId);
      return;
    }
    const zone = floor?.zones?.find((z) => z.id === zoneId);
    if (!zone) {
      console.error("Zone not found for ID:", zoneId);
      return;
    }
    delete floor.zones;
    let locationObjWithZone = {
      ...locationObj,
      floor: { ...floor, zone: zone },
    };
    delete locationObjWithZone.floors;
    dispatch(
      updateUI({
        formData: {
          ...formData,
          step3: {
            ...formData.step3,
            [name]: value,
            location: locationObjWithZone,
          },
        },
      })
    );
  };

  const handleSelectProfile = (id) => {
    let deviceProfile = Array.isArray(formData?.step2?.deviceProfiles)
      ? formData?.step2?.deviceProfiles
      : [];
    const attributes = deviceProfile
      ?.filter((profile) => profile?.profileid === id)
      ?.flatMap((profile) => profile?.attributes || [])
      ?.map((attribute) => ({
        key: attribute.profileid,
        attribute: `${attribute.name}(${attribute.unit})`,
        minimum: attribute.min,
        maximum: attribute.max,
        alertType: attribute.alertType || "N/A",
        alertGroup: attribute.alertGroup?.join(", ") || "N/A",
      }));

    let locations = deviceProfile?.flatMap(
      (profile) => profile?.locations || []
    );

    if (Array.isArray(locations) && locations.length > 0) {
      if (
        Array.isArray(locations[0]?.locationZone) &&
        locations[0]?.locationZone?.length > 0
      ) {
        locations = locations[0]?.locationZone?.map((loc, index) => ({
          key: `loc-${index}`,
          zone: loc,
          trigger: locations[0]?.trigger || "N/A",
          alerttype: locations[0]?.alerttype || "N/A",
          alertgroup: Array.isArray(locations[0]?.alertgroup)
            ? locations[0].alertgroup?.join(", ")
            : "N/A",
        }));
      }
    }

    const frequencies = deviceProfile
      ?.filter((profile) => profile?.profileid === id)
      ?.map((profile) => profile?.frequencies || {});

    dispatch(
      updateUI({
        opType: "selectProfile",
        formData: {
          ...formData,
          step2: {
            ...formData.step2,
            attributes,
            locations,
            frequencies: frequencies?.length > 0 ? frequencies[0] : {},
          },
        },
      })
    );
  };

  return (
    <>
      <ModalTitle>
        {opType !== "create" && (
          <Title size="large" bold={true} color="#000">
            {opHeaderText} Asset
          </Title>
        )}
        {opHeaderText === "create" && (
          <Body size="large">{opHeaderText} asset</Body>
        )}
        <div className="mt-4">
          <Title size="small" bold={true} color="#6F7171">
            Asset details
          </Title>
        </div>
      </ModalTitle>
      <ModalBody>
        <div className="d-flex flex-column justify-content-center align-items-center gap-3">
          <Input
            type="text"
            label="Asset name"
            name="assetName"
            defaultValue={formData?.step1?.assetName}
            readOnly={false}
            required={true}
            disabled={false}
            error={formData?.step1?.errors?.assetName}
            errorText="Asset name is required."
            onChange={handleFieldChange}
          />
          <Input
            type="text"
            label="Description"
            name="description"
            defaultValue={formData?.step1?.description}
            readOnly={false}
            required={false}
            disabled={false}
            onChange={handleFieldChange}
          />
        </div>

        <div className="d-flex flex-column justify-content-center align-items-center gap-3 mt-3">
          <div style={{ textAlign: "left", width: "100%" }}>
            <Title size="small" bold={true} color="#6F7171">
              Device details
            </Title>
          </div>
          <div className="w-100 d-flex flex-column justify-content-start align-items-start">
            <Body size="small">Device model</Body>
            <Select
              className={
                formData?.step2?.errors?.deviceType
                  ? "shipment-form-fields shipment-form-field-error"
                  : "shipment-form-fields"
              }
              style={{
                marginTop: "8px",
              }}
              placeholder="Select device type"
              value={formData?.step2?.deviceType}
              options={[
                { value: "", label: "Select device type" },
                ...(Array.isArray(formData?.step2?.deviceTypes)
                  ? formData?.step2?.deviceTypes
                  : []
                ).map((type) => ({
                  label: type.devicetype,
                  value: type.devicetype,
                })),
              ]}
              onChange={(value) => {
                dispatch(fetchDeviceProfilesRequest(value));
                handleSelectChange("deviceType", value);
              }}
            />
            {formData?.step2?.errors?.deviceType && (
              <div style={{ paddingTop: 5 }}>
                <ErrorMessage
                  fieldName="devicetype"
                  errors={{ devicetype: "Device type is required" }}
                />
              </div>
            )}
          </div>
          <div className="w-100 d-flex flex-column justify-content-start align-items-start">
            <Body size="small">Device ID</Body>
            <Select
              className={
                formData?.step2?.errors?.deviceId
                  ? "shipment-form-fields shipment-form-field-error"
                  : "shipment-form-fields"
              }
              style={{
                marginTop: "8px",
              }}
              placeholder="Select device Name"
              value={formData?.step2?.deviceId?.id || ""}
              options={[
                { value: "", label: "Select device name" },
                ...(formData?.step2?.deviceType &&
                Array.isArray(formData?.step2?.deviceNames)
                  ? formData?.step2?.deviceNames?.filter(
                      (dname) =>
                        dname?.devicetype === formData?.step2?.deviceType
                    )
                  : []
                ).filter((dname) => dname?.customdata?.deviceid === formData?.step2?.deviceId?.id || dname?.customdata?.isAvailable).map((dname) => ({
                  label: dname.name,
                  value: dname?.customdata?.deviceid,
                  bleserial: dname.serial,
                })),
              ]}
              onChange={(_, option) => {
                handleSelectChange(
                  "deviceId",
                  {
                    id: option.value,
                    name: option.label,
                  },
                  option.bleserial
                );
              }}
            />
            {formData?.step2?.errors?.deviceId && (
              <div style={{ paddingTop: 5 }}>
                <ErrorMessage
                  fieldName="deviceId"
                  errors={{ deviceId: "Device ID is required" }}
                />
              </div>
            )}
          </div>
          <div className="w-100 d-flex flex-column justify-content-start align-items-start">
            <div style={{ textAlign: "left", width: "100%" }}>
              <Body size="small" bold={false} color="#000">
                Device Profile
              </Body>
            </div>
            <button
              onClick={() =>
                handleSelectProfile(formData?.step2?.deviceProfile?.id)
              }
              className={
                formData?.step2?.errors?.deviceProfile
                  ? "w-100 d-flex justify-content-between align-items-center mt-1"
                  : "w-100 d-flex justify-content-between align-items-center mt-1 bg-transparent"
              }
              style={
                formData?.step2?.errors?.deviceProfile
                  ? {
                      padding: "11px",
                      border: "1px solid #ba2626",
                      backgroundColor: "#ffece0",
                      borderRadius: "4px",
                    }
                  : {
                      padding: "11px",
                      border: "1px solid #000",
                      borderRadius: "4px",
                    }
              }
            >
              <Body size="large" bold={false} color="#000">
                {formData?.step2?.deviceProfile?.name || ""}
              </Body>
              <Icon name="right-arrow" size="medium" color="#000" />
            </button>
            {formData?.step2?.errors?.deviceProfile && (
              <div style={{ paddingTop: 5 }}>
                <ErrorMessage
                  fieldName="deviceProfile"
                  errors={{ deviceProfile: "Device Profile is required" }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="d-flex flex-column justify-content-center align-items-center gap-3 mt-3">
          <div style={{ textAlign: "left", width: "100%" }}>
            <Title size="small" bold={true} color="#6F7171">
              Additional details
            </Title>
          </div>
          <div className="w-100 d-flex flex-column justify-content-start align-items-start mb-4">
            <div className="d-flex flex-row justify-content-start align-items-center gap-1">
              <Body size="small">Location zone</Body>
              <Body size="small" color="#6F7171">
                Optional
              </Body>
            </div>
            <Select
              className="shipment-form-fields"
              style={{
                marginTop: "8px",
              }}
              defaultValue={formData?.step3?.locationZone}
              options={[
                {
                  key: "empty-location-zone",
                  label: "No location zone",
                  value: "",
                  locationObj: null,
                },
                ...getLocationZoneList(formData?.step3?.locations).map(
                  (location) => ({
                    key: location.id,
                    label: location.name,
                    value: location.name,
                    locationObj: location.location,
                  })
                ),
              ]}
              onChange={(value, option) => {
                handleLocationZoneFieldChange(
                  option.key,
                  "locationZone",
                  value,
                  option.locationObj
                );
              }}
            />
          </div>
          <div className="w-100 d-flex justify-content-start align-items-start gap-3">
            <Input
              type="text"
              label="Asset type"
              name="assetType"
              defaultValue={formData?.step3?.assetType}
              readOnly={false}
              required={false}
              disabled={false}
              onChange={handleFAdditionalFieldChange}
            />
            <Input
              type="text"
              label="Asset model"
              name="assetModel"
              defaultValue={formData?.step3?.assetModel}
              readOnly={false}
              required={false}
              disabled={false}
              onChange={handleFAdditionalFieldChange}
            />
          </div>
          <div className="w-100 d-flex justify-content-start align-items-start gap-3">
            <Input
              type="text"
              label="Asset serial number"
              name="assetSerialNumber"
              defaultValue={formData?.step3?.assetSerialNumber}
              readOnly={false}
              required={false}
              disabled={false}
              onChange={handleFAdditionalFieldChange}
            />
            <Input
              type="text"
              label="Asset owner"
              name="assetOwner"
              defaultValue={formData?.step3?.assetOwner}
              readOnly={false}
              required={false}
              disabled={false}
              onChange={handleFAdditionalFieldChange}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        buttonData={{
          close: {
            width: "100%",
            children: "Next",
            onClick: handleNext,
          },
          primary: {
            children: "Cancel",
            width: "100%",
            onClick: onExit,
          },
        }}
      />
    </>
  );
}

EditAssetStep1.propTypes = {
  opHeaderText: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  onExit: PropTypes.func.isRequired,
};

export default EditAssetStep1;
